
import React, { useRef, useEffect, useState } from 'react';
import { ChatMessageList } from '@/components/chat/ChatMessageList';
import { ChatInput } from '@/components/ai/chat/ChatInput';
import { EmptyChat } from '@/components/ai/chat/EmptyChat';
import { ContextError } from '@/components/ai/chat/ContextError';
import { ChatErrorBoundary } from '@/components/ai/chat/ErrorBoundary';
import { useChatContext } from '@/hooks/ai/useChatContext';
import { useAuth } from '@/contexts/AuthContext';
import { Message } from './types';
import { CalendarEvent } from '@/types/calendar';

interface EnhancedChatSectionProps {
  messages: Message[];
  onSendMessage: (message: string) => void;
  isProcessing: boolean;
  event?: CalendarEvent;
}

export const EnhancedChatSection: React.FC<EnhancedChatSectionProps> = ({
  messages,
  onSendMessage,
  isProcessing,
  event
}) => {
  const { session } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [contextError, setContextError] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  // Generate a chat ID based on the event or use a default
  const chatId = event?.id || 'general-chat';
  
  // Use chat context hook
  const { contextData, isLoadingContext } = useChatContext({
    chatId,
    messages,
    session,
    setContextError
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end'
      });
    }
  }, [messages]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ 
          behavior: 'auto',
          block: 'end'
        });
      }, 100);
    }
  }, []);

  const handleSendMessage = () => {
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  if (contextError) {
    return <ContextError />;
  }

  return (
    <ChatErrorBoundary>
      <div 
        ref={containerRef}
        className="flex flex-col h-full w-full overflow-hidden"
      >
        {/* Chat Messages Container - Takes most of the space */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {messages.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-4">
              <EmptyChat />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto overscroll-behavior-contain">
              <div className="min-h-full flex flex-col">
                <div className="flex-1" />
                <div className="px-4 py-2 space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex items-start space-x-3 ${
                        message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                      }`}
                    >
                      <div className={`flex-1 max-w-[85%] ${
                        message.sender === 'user' ? 'text-right' : 'text-left'
                      }`}>
                        <div className={`p-3 rounded-lg break-words ${
                          message.sender === 'user'
                            ? 'bg-primary text-primary-foreground ml-auto'
                            : 'bg-muted'
                        }`}>
                          <div className="whitespace-pre-wrap overflow-wrap-anywhere">
                            {message.content}
                          </div>
                        </div>
                        <p className={`text-xs text-muted-foreground mt-1 ${
                          message.sender === 'user' ? 'text-right' : 'text-left'
                        }`}>
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {isProcessing && (
                    <div className="flex items-start space-x-3">
                      <div className="flex-1 max-w-[85%]">
                        <div className="bg-muted p-3 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                              <div className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                              <div className="w-2 h-2 bg-current rounded-full animate-bounce"></div>
                            </div>
                            <span className="text-sm text-muted-foreground">AI is thinking...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div ref={messagesEndRef} className="h-px" />
              </div>
            </div>
          )}
        </div>

        {/* Chat Input - Fixed at bottom */}
        <div className="flex-shrink-0 border-t bg-background">
          <div className="p-4">
            <ChatInput
              inputValue={inputValue}
              setInputValue={setInputValue}
              handleSendMessage={handleSendMessage}
              isDisabled={isProcessing}
            />
          </div>
        </div>
      </div>
    </ChatErrorBoundary>
  );
};
